/* Product choices use Shopify variants and plan allocations. Existing cart items are preserved. */
if (!customElements.get('jive-product-form')) {
  customElements.define('jive-product-form', class extends HTMLElement {
    connectedCallback() {
      if (this.dataset.enhanced) return;
      const dataElement = this.querySelector('[data-product-data]');
      if (!dataElement) return;
      try { this.data = JSON.parse(dataElement.textContent); } catch (_) { return; }
      this.form = this.querySelector('form');
      this.variantInput = this.form.querySelector('[name="id"]');
      this.planInput = this.form.querySelector('[name="selling_plan"]');
      this.quantityInput = this.form.querySelector('[name="quantity"]');
      this.submitButton = this.form.querySelector('[type="submit"]');
      this.errorElement = this.querySelector('.jive-product-main__error');
      this.promotionNote = this.querySelector('.jive-product-main__promo')?.textContent || '';
      this.money = new Intl.NumberFormat(document.documentElement.lang || 'en-US', { style: 'currency', currency: this.dataset.currency || 'USD' });
      this.controller = new AbortController();
      this.bundle = this.querySelector('jive-protein-bundle');
      if (this.bundle && !this.bundle.initialize) return;
      if (this.bundle) this.bundle.initialize(this);
      const options = { signal: this.controller.signal };
      this.form.addEventListener('change', () => this.update(), options);
      this.quantityInput.addEventListener('input', () => this.update(false), options);
      this.form.addEventListener('submit', (event) => this.add(event), options);
      document.addEventListener('click', (event) => {
        const link = event.target.closest('[data-flavor-choice]');
        if (!link) return;
        if (this.bundle) {
          if (!this.bundle.chooseFlavor(link.dataset.flavorChoice)) return;
          event.preventDefault();
          this.form.scrollIntoView({ block: 'start', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
          return;
        }
        const choice = [...this.querySelectorAll('[data-option-index="0"] input')].find((input) => input.value === link.dataset.flavorChoice);
        if (!choice || choice.disabled) return;
        event.preventDefault();
        choice.checked = true;
        this.update();
        this.form.scrollIntoView({ block: 'start', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
        choice.focus({ preventScroll: true });
      }, options);
      this.querySelectorAll('[data-image]').forEach((button) => button.addEventListener('click', () => this.showImage(button.dataset.image, button.dataset.imageAlt), options));
      this.gallery = this.querySelector('[data-gallery-track]');
      if (this.gallery) {
        const updateGallery = () => {
          const end = this.gallery.scrollWidth - this.gallery.clientWidth;
          this.querySelector('[data-gallery-prev]').disabled = this.gallery.scrollLeft < 5;
          this.querySelector('[data-gallery-next]').disabled = this.gallery.scrollLeft > end - 5;
        };
        for (const [selector, direction] of [['[data-gallery-prev]', -1], ['[data-gallery-next]', 1]]) {
          this.querySelector(selector)?.addEventListener('click', () => {
            const slides = [...this.gallery.children];
            const start = this.gallery.getBoundingClientRect().left;
            const current = slides.reduce((nearest, slide, index) => Math.abs(slide.getBoundingClientRect().left - start) < Math.abs(slides[nearest].getBoundingClientRect().left - start) ? index : nearest, 0);
            const target = Math.max(0, Math.min(slides.length - 1, current + direction));
            const left = this.gallery.scrollLeft + slides[target].getBoundingClientRect().left - start;
            this.gallery.scrollTo({ left, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
          }, options);
        }
        this.gallery.addEventListener('scroll', updateGallery, { ...options, passive: true });
        window.addEventListener('resize', updateGallery, options);
        updateGallery();
      }
      this.stickyBar = this.querySelector('[data-sticky-bar]');
      if (this.stickyBar) {
        const orderRow = this.querySelector('.jive-product-main__order-row');
        const footer = document.querySelector('.jive-refresh-footer');
        const updateSticky = () => {
          const footerVisible = footer && footer.getBoundingClientRect().top < innerHeight;
          this.stickyBar.hidden = orderRow.getBoundingClientRect().bottom >= 0 || footerVisible;
        };
        window.addEventListener('scroll', updateSticky, { ...options, passive: true });
        window.addEventListener('resize', updateSticky, options);
        updateSticky();
        this.querySelector('[data-sticky-purchase]').addEventListener('click', () => {
          this.form.scrollIntoView({ block: 'start', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
          const choice = this.form.querySelector('[data-option-index] input:checked') || this.form.querySelector('[name="purchase_option"]:checked') || this.variantInput;
          choice.focus({ preventScroll: true });
        }, options);
      }
      this.dataset.enhanced = 'true';
      this.update();
    }
    disconnectedCallback() { this.controller?.abort(); this.purchaseObserver?.disconnect(); delete this.dataset.enhanced; }
    format(cents) { return this.money.format(cents / 100); }
    showImage(src, alt) {
      const image = this.querySelector('.jive-product-main__image');
      if (!image || !src) return;
      image.removeAttribute('srcset');
      image.src = src;
      image.alt = alt || '';
      image.classList.toggle('jive-product-main__image--cutout', /(?:hero-creatine-sticks\.png|jive-creatine-sticks\.webp)/.test(src));
      this.gallery?.scrollTo({ left: 0, behavior: 'instant' });
      this.querySelectorAll('[data-image]').forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.image.split('?')[0] === src.split('?')[0])));
    }
    update(changeImage = true) {
      if (this.bundle) { this.bundle.update(changeImage); return; }
      const values = [...this.querySelectorAll('[data-option-index]')].map((fieldset) => fieldset.querySelector('input:checked')?.value);
      this.variant = values.length ? this.data.variants.find((variant) => variant.options.every((value, index) => value === values[index])) : this.data.variants.find((variant) => String(variant.id) === this.variantInput.value);
      const variant = this.variant;
      const qty = Math.max(1, Math.floor(Number(this.quantityInput.value) || 1));
      if (variant) this.variantInput.value = variant.id;
      const allocations = variant?.selling_plan_allocations || [];
      if (this.planInput) {
        const oldPlan = this.planInput.value;
        this.planInput.replaceChildren(...allocations.map((allocation) => {
          const plan = this.data.plans.flatMap((group) => group.selling_plans).find((entry) => entry.id === allocation.selling_plan_id);
          return new Option(`${plan?.name || ''} · ${this.format(allocation.price)}`, allocation.selling_plan_id);
        }));
        if (allocations.some((entry) => String(entry.selling_plan_id) === oldPlan)) this.planInput.value = oldPlan;
        const subInput = this.querySelector('[value="subscription"]');
        if (subInput) {
          subInput.disabled = allocations.length === 0;
          if (subInput.checked && !allocations.length) {
            const once = this.querySelector('[value="once"]');
            if (once) once.checked = true;
          }
        }
      }
      this.subscription = this.form.querySelector('[name="purchase_option"]:checked')?.value === 'subscription';
      this.allocation = this.subscription ? allocations.find((entry) => String(entry.selling_plan_id) === this.planInput?.value) : null;
      const base = variant?.price || 0;
      const subBase = allocations.find((entry) => String(entry.selling_plan_id) === this.planInput?.value)?.price ?? allocations[0]?.price ?? base;
      this.offerEligible = !this.data.offerPlan || this.planInput?.value === String(this.data.offerPlan);
      const offer = this.offerEligible ? (this.data.firstOrderDiscount || 0) * (this.data.firstOrderDiscountEach ? qty : 1) : 0;
      this.querySelector('[data-once-price]')?.replaceChildren(document.createTextNode(this.format(base * qty)));
      this.querySelector('[data-subscription-price]')?.replaceChildren(document.createTextNode(this.format(Math.max(0, subBase * qty - offer))));
      const cadence = this.querySelector('[data-cadence]');
      if (cadence) cadence.hidden = !this.subscription;
      const offerNote = this.querySelector('[data-subscription-offer]');
      if (offerNote) offerNote.hidden = !this.subscription || !this.offerEligible;
      const promotionNote = this.querySelector('.jive-product-main__promo');
      if (promotionNote) promotionNote.textContent = this.data.firstOrderDiscount && (!this.subscription || !this.offerEligible) ? this.data.labels.tax : this.promotionNote;
      if (this.planInput) this.planInput.disabled = !this.subscription;
      if (this.subscription && this.allocation) {
        const plan = this.data.plans.flatMap((group) => group.selling_plans).find((entry) => entry.id === this.allocation.selling_plan_id);
        const regular = this.allocation.price_adjustments?.at(-1)?.price ?? this.allocation.price;
        this.querySelector('[data-recurring-terms]').textContent = `${this.data.labels.recurring} ${this.format(regular * qty)}. ${plan?.name || ''}. ${this.data.labels.tax}`;
      }
      this.total = this.allocation ? Math.max(0, this.allocation.price * qty - offer) : base * qty;
      this.querySelector('[data-total-price]').textContent = this.format(this.total);
      this.querySelector('[data-sticky-price]')?.replaceChildren(document.createTextNode(this.format(this.total)));
      const stickySummary = this.querySelector('[data-sticky-summary]');
      if (stickySummary) {
        const cadenceName = this.data.plans.flatMap((group) => group.selling_plans).find((entry) => entry.id === this.allocation?.selling_plan_id)?.name;
        const description = variant?.options.filter((value) => value !== 'Default Title').join(' / ') || this.querySelector('.jive-product-main__title')?.textContent || '';
        stickySummary.textContent = `${qty} × ${description} · ${this.subscription ? cadenceName || '' : this.data.labels.once || ''}`;
      }
      this.querySelector('[data-heading-price]').textContent = this.format(base);
      if (variant?.options.length > 1) this.querySelector('[data-heading-size]').textContent = variant.options[1];
      const available = variant?.available && (!this.subscription || this.allocation);
      this.submitButton.disabled = !available || this.busy;
      this.querySelector('[data-add-label]').textContent = this.busy ? this.data.labels.adding : !variant ? this.data.labels.unavailable : !available ? this.data.labels.soldOut : this.data.labels.add;
      const note = this.querySelector('[data-flavor-note]');
      if (note) note.textContent = this.data.flavors.find((flavor) => values.includes(flavor.name))?.note || '';
      this.querySelectorAll('[data-option-index]').forEach((fieldset) => {
        const index = Number(fieldset.dataset.optionIndex);
        fieldset.querySelectorAll('input').forEach((input) => {
          const candidate = this.data.variants.find((entry) => entry.options.every((value, position) => value === (position === index ? input.value : values[position])));
          input.disabled = !candidate || !candidate.available;
          input.parentElement.title = input.disabled ? this.data.labels.soldOut : '';
        });
      });
      const flavorImage = this.data.flavors.find((flavor) => values.includes(flavor.name));
      const image = flavorImage?.image ? { src: flavorImage.image, alt: flavorImage.name } : variant?.featured_image;
      if (changeImage && image && this.lastVariant !== variant.id) this.showImage(image.src, image.alt || variant.name || '');
      this.lastVariant = variant?.id;
    }
    async add(event) {
      event.preventDefault();
      if (this.busy || (this.bundle ? !this.bundle.getItems().length : !this.variant?.available) || !this.form.reportValidity()) return;
      this.busy = true;
      this.errorElement.hidden = true;
      this.update(false);
      // Keep the submitted purchase and its offer together if choices change while requests are pending.
      const subscription = this.subscription;
      const offerEligible = this.offerEligible;
      const routeRoot = window.Shopify?.routes?.root || '/';
      const item = { id: this.variant.id, quantity: Number(this.quantityInput.value) || 1 };
      if (subscription && this.allocation) item.selling_plan = this.allocation.selling_plan_id;
      try {
        const giftVariant = subscription && offerEligible ? Number(this.data.subscriptionGift) : 0;
        let existingCart = null;
        let giftAdded = false;
        const productItems = this.bundle ? this.bundle.getItems() : [item];
        const items = [...productItems];
        const quantity = productItems.reduce((total, line) => total + line.quantity, 0);
        const propertiesKey = (properties) => JSON.stringify(Object.entries(properties || {})
          .filter(([, value]) => value != null && value !== '').map(([key, value]) => [key, String(value)])
          .sort(([a], [b]) => a.localeCompare(b)));
        const planId = (line) => String(line.selling_plan_allocation?.selling_plan?.id ?? line.selling_plan ?? 0);
        // Snapshot the price shown for each submitted flavor before any requests.
        // A cheaper existing offer can legitimately replace our configured code.
        const promises = productItems.map((line) => ({
          id: String(line.id),
          plan: planId(line),
          properties: propertiesKey(line.properties),
          quantity: line.quantity,
          minimumQuantity: line.quantity,
          total: this.bundle ? this.bundle.prices(quantity, subscription, [{
            variant: this.data.variants.find((variant) => String(variant.id) === String(line.id)),
            quantity: line.quantity,
          }]).total : this.total,
        }));
        if (giftVariant) {
          const beforeResponse = await fetch(`${routeRoot}cart.js`, { headers: { Accept: 'application/json' } });
          if (!beforeResponse.ok) throw new Error(this.data.labels.error);
          existingCart = await beforeResponse.json();
          if (!(existingCart.items || []).some((line) => line.variant_id === giftVariant && line.properties?._free_gift === 'Yes')) {
            items.push({ id: giftVariant, quantity: 1, properties: { _free_gift: 'Yes' } });
            giftAdded = true;
          }
        }
        const response = await fetch(`${routeRoot}cart/add.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items }) });
        const result = await response.json().catch(() => ({ description: this.data.labels.error }));
        if (!response.ok) throw new Error(result.description || this.data.labels.error);
        // Shopify returns the merged quantity on add. Keys can change when
        // discounts change, so verify variant, plan and properties instead.
        for (const promise of promises) {
          const added = result.items?.find((line) => String(line.variant_id ?? line.id) === promise.id &&
            planId(line) === promise.plan && propertiesKey(line.properties) === promise.properties);
          if (Number.isInteger(added?.quantity) && added.quantity >= promise.quantity) promise.minimumQuantity = added.quantity;
        }
        // Keep existing promotion codes when applying the offers configured on the original PDP.
        let promotionFailed = false;
        const subscriptionCodes = subscription && offerEligible ? (this.data.subscriptionCodes || '').split(/\n/).map((code) => code.trim()).filter(Boolean) : [];
        const codes = [this.data.discountCode, ...subscriptionCodes].filter(Boolean);
        if (this.data.promoParam) {
          if (new URLSearchParams(location.search).get('promo') === this.data.promoParam) document.cookie = `jive_promo=${encodeURIComponent(this.data.promoParam)}; max-age=432000; path=/; SameSite=Lax`;
          const active = document.cookie.split(';').some((part) => part.trim() === `jive_promo=${encodeURIComponent(this.data.promoParam)}`);
          if (active) codes.push(...(this.data.promoCodes || '').split(/\n/).map((code) => code.trim()).filter(Boolean));
        }
        if (codes.length) {
          let offerCart = null;
          try {
            const cartResponse = existingCart ? null : await fetch(`${routeRoot}cart.js`, { headers: { Accept: 'application/json' } });
            if (cartResponse && !cartResponse.ok) throw new Error('Promotion unavailable');
            const cart = existingCart || await cartResponse.json();
            let existing = (cart.discount_codes || []).filter((code) => code.applicable).map((code) => code.code);
            const hasRequiredOffer = (candidate) => Array.isArray(candidate?.items) && promises.length > 0 &&
              promises.every((promise) => {
                if (!Number.isInteger(promise.total) || promise.total < 0) return false;
                const lines = candidate.items.filter((line) => String(line.variant_id ?? line.id) === promise.id &&
                  planId(line) === promise.plan && propertiesKey(line.properties) === promise.properties);
                return lines.length > 0 && lines.every((line) => {
                  if (!Number.isInteger(line.quantity) || line.quantity < 1 ||
                    !Number.isInteger(line.final_line_price) || line.final_line_price < 0) return false;
                  // Check each line independently; another product's savings
                  // must not hide an overpriced item. Merged quantities can
                  // change Shopify's allocation rounding by a cent.
                  const ceiling = line.quantity === promise.quantity ? promise.total :
                    Math.ceil(promise.total * line.quantity / promise.quantity) + 1;
                  return line.final_line_price <= ceiling;
                }) && lines.reduce((total, line) => total + line.quantity, 0) >= promise.minimumQuantity;
              }) && (!giftVariant || candidate.items.some((line) => line.variant_id === giftVariant &&
                line.properties?._free_gift === 'Yes' && line.quantity > 0 && line.final_line_price === 0));
            let verified = false;
            // A lost or stale update response is not proof that Shopify rejected the
            // offer. Re-read the cart, then retry only the idempotent discount update.
            // Never retry cart/add.js: that would duplicate the shopper's items.
            for (let attempt = 0; attempt < 2 && !verified; attempt++) {
              try {
                const discountResponse = await fetch(`${routeRoot}cart/update.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ discount: [...new Set([...existing, ...codes])].join(',') }) });
                if (discountResponse.ok) {
                  offerCart = await discountResponse.json();
                  verified = hasRequiredOffer(offerCart);
                }
              } catch (_) { /* A fresh read below can confirm a successfully applied offer. */ }
              if (verified) break;
              try {
                const freshResponse = await fetch(`${routeRoot}cart.js`, { cache: 'no-store', headers: { Accept: 'application/json' } });
                if (freshResponse.ok) {
                  offerCart = await freshResponse.json();
                  verified = hasRequiredOffer(offerCart);
                  existing = [...new Set([...existing, ...(offerCart.discount_codes || []).filter((code) => code.applicable).map((code) => code.code)])];
                }
              } catch (_) { /* Preserve the existing recovery path if verification still fails. */ }
            }
            if (!verified) throw new Error('Promotion unavailable');
          } catch (_) {
            promotionFailed = true;
            let giftRecoveryFailed = false;
            // An offer failure must never leave a newly added gift charged as a normal item.
            if (giftAdded) {
              try {
                const currentCart = offerCart || await fetch(`${routeRoot}cart.js`, { headers: { Accept: 'application/json' } }).then((response) => response.json());
                const addedGift = (currentCart.items || []).find((line) => line.variant_id === giftVariant && line.properties?._free_gift === 'Yes');
                if (addedGift && addedGift.final_line_price > 0) {
                  const removal = await fetch(`${routeRoot}cart/change.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ id: addedGift.key, quantity: 0 }) });
                  if (!removal.ok) giftRecoveryFailed = true;
                }
              } catch (_) { giftRecoveryFailed = true; }
            }
            const reviewLink = document.createElement('a');
            reviewLink.href = `${routeRoot}cart`;
            reviewLink.textContent = this.data.labels.reviewCart;
            const recoveryMessage = `${this.data.labels.promotionError}${giftRecoveryFailed ? ` ${this.data.labels.giftRecoveryError}` : ''} `;
            this.errorElement.replaceChildren(document.createTextNode(recoveryMessage), reviewLink);
            this.errorElement.hidden = false;
          }
        }
        document.dispatchEvent(new CustomEvent(promotionFailed ? 'cart:refresh' : 'cart:add', { bubbles: true, detail: result }));
      } catch (error) {
        this.errorElement.textContent = error.message || this.data.labels.error;
        this.errorElement.hidden = false;
      } finally {
        this.busy = false;
        this.update(false);
      }
    }
  });
}
